import { Box, Chip, Paper, Stack, Typography, type SxProps } from '@mui/material';
import React from 'react';
import type { Holder } from '../data/types';

export function InvestorTitle({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ sm: 'flex-end' }}
      spacing={1.5}
      sx={{ mb: 2.5 }}
    >
      <Box>
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
  );
}

export function Card({
  children,
  sx,
  onClick,
}: {
  children: React.ReactNode;
  sx?: SxProps;
  onClick?: () => void;
}) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => onClick && event.key === 'Enter' && onClick()}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

export function shortEntityName(holder: Holder): string {
  if (holder.type === 'INDIVIDUAL') return holder.name;
  const atf = holder.name.split(' ATF ');
  return atf.length > 1 ? atf[1] : holder.name;
}

export function EntityFilter({
  holders,
  value,
  onChange,
}: {
  holders: Holder[];
  value: string;
  onChange: (next: string) => void;
}) {
  if (holders.length < 2) return null;
  return (
    <Stack useFlexGap direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
      <Chip
        label="All entities"
        color={value === 'ALL' ? 'primary' : 'default'}
        variant={value === 'ALL' ? 'filled' : 'outlined'}
        onClick={() => onChange('ALL')}
      />
      {holders.map((holder) => (
        <Chip
          key={holder.id}
          label={shortEntityName(holder)}
          color={value === holder.id ? 'primary' : 'default'}
          variant={value === holder.id ? 'filled' : 'outlined'}
          onClick={() => onChange(holder.id)}
        />
      ))}
    </Stack>
  );
}

export function SectionLabel({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mt: 3, mb: 1.25 }}
    >
      <Typography variant="h6">{children}</Typography>
      {action}
    </Stack>
  );
}
